/**
 * DOC HELPER — artifact structure state reconciliation.
 *
 * Revision identity can exclude stale structure, but local renderability must
 * never remove the backend's explicit structure-unavailable result.
 */

const STRUCTURE_MAP_KEYS = ['semantic_map', 'procurement_semantic_map', 'structure_map'];
const STRUCTURE_LIST_KEYS = ['procurement_packages', 'field_pack_suggestions'];
const STRUCTURE_UNAVAILABLE_KEYS = ['structure_unavailable', 'structureUnavailable'];
const STRUCTURE_FIELD_KEYS = [
  ...STRUCTURE_MAP_KEYS,
  ...STRUCTURE_LIST_KEYS,
  ...STRUCTURE_UNAVAILABLE_KEYS,
  'metadata',
];

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function resolveText(value) {
  return String(value ?? '').trim();
}

function artifactRevisionId(document = {}) {
  const artifact = resolveObject(document);
  const latestRevision = resolveObject(artifact.latest_revision || artifact.latestRevision);
  return resolveText(artifact.revision_id || artifact.revisionId || latestRevision.revision_id || latestRevision.revisionId);
}

function semanticMapSourceRevisionId(semanticMap = {}) {
  const item = resolveObject(semanticMap);
  return resolveText(item.source_revision_id || item.sourceRevisionId || item.revision_id || item.revisionId);
}

function semanticMapMatchesRevision(semanticMap = {}, revisionId = '') {
  const sourceRevisionId = semanticMapSourceRevisionId(semanticMap);
  return !revisionId || !sourceRevisionId || sourceRevisionId === revisionId;
}

function withoutStaleSemanticStructure(document = {}) {
  const artifact = resolveObject(document);
  const revisionId = artifactRevisionId(artifact);
  if (!revisionId) {
    return artifact;
  }
  const result = STRUCTURE_MAP_KEYS.reduce((next, key) => {
    const semanticMap = resolveObject(next[key]);
    if (Object.keys(semanticMap).length > 0 && !semanticMapMatchesRevision(semanticMap, revisionId)) {
      const { [key]: _staleMap, ...rest } = next;
      void _staleMap;
      return rest;
    }
    return next;
  }, artifact);
  const hadSemanticMap = STRUCTURE_MAP_KEYS.some((key) => Object.keys(resolveObject(artifact[key])).length > 0);
  const hasCurrentSemanticMap = STRUCTURE_MAP_KEYS.some((key) => Object.keys(resolveObject(result[key])).length > 0);
  if (!hadSemanticMap || hasCurrentSemanticMap) {
    return result;
  }
  return STRUCTURE_LIST_KEYS.reduce((next, key) => {
    const { [key]: _staleList, ...rest } = next;
    void _staleList;
    return rest;
  }, result);
}

function hasStructureFieldValue(value) {
  const item = Array.isArray(value) ? value : resolveObject(value);
  if (Array.isArray(item)) {
    return item.length > 0;
  }
  if (Object.keys(item).length > 0) {
    return true;
  }
  return value !== undefined && value !== null && value !== '';
}

function treeNodeCount(nodes = []) {
  return resolveArray(nodes).reduce((count, node) => (
    count + 1 + treeNodeCount(resolveObject(node).children)
  ), 0);
}

function hasTreeNodes(semanticMap = {}) {
  const tree = resolveObject(resolveObject(semanticMap).tree);
  const branches = resolveArray(tree.branches);
  const treeData = resolveArray(tree.treeData);
  return branches.some((branch) => resolveArray(resolveObject(branch).nodes).length > 0)
    || treeNodeCount(treeData) > 1;
}

function hasGroupItems(semanticMap = {}) {
  return resolveArray(resolveObject(semanticMap).groups)
    .some((group) => resolveArray(resolveObject(group).items).length > 0);
}

function hasSemanticStructure(document = {}) {
  const artifact = withoutStaleSemanticStructure(document);
  return STRUCTURE_MAP_KEYS
    .map((key) => resolveObject(artifact[key]))
    .some((semanticMap) => Object.keys(semanticMap).length > 0 && (
      hasTreeNodes(semanticMap) || hasGroupItems(semanticMap)
    ));
}

export function hasRenderableArtifactStructure(document = {}) {
  const artifact = withoutStaleSemanticStructure(document);
  return STRUCTURE_LIST_KEYS.some((key) => resolveArray(artifact[key]).length > 0)
    || hasSemanticStructure(artifact);
}

export function normalizeArtifactStructureState(document = {}) {
  return withoutStaleSemanticStructure(document);
}

export function artifactStructureFields(document = {}) {
  const artifact = withoutStaleSemanticStructure(document);
  return STRUCTURE_FIELD_KEYS.reduce((fields, key) => (
    hasStructureFieldValue(artifact[key])
      ? { ...fields, [key]: artifact[key] }
      : fields
  ), {});
}

export function mergeArtifactStructureFields(...sources) {
  const merged = sources
    .map((source) => artifactStructureFields(source))
    .reduce((fields, sourceFields) => ({ ...fields, ...sourceFields }), {});
  return normalizeArtifactStructureState(merged);
}

export default normalizeArtifactStructureState;
